/**
 * Normalize instrument names for consistent matching across different sources
 * Enhanced to handle common variations in instrument naming while preserving USD
 */
export const normalizeInstrument = (instrument: string): string => {
  if (!instrument) return '';
  
  return instrument
    .toString()
    .trim()
    .toUpperCase()
    .replace(/\s+/g, '') // Remove spaces
    .replace(/[^A-Z0-9]/g, '') // Remove non-alphanumeric characters
    .replace(/USDT/g, 'USD') // Standardize USDT to USD
    .replace(/FOREX/g, ''); // Remove common prefix
};

/**
 * Create instrument mapping from merge analysis config
 * Enhanced to support both old and new config structures
 */
export const createInstrumentMapping = (config: any): Map<string, { childBotId: string; childBotName?: string }> => {
  const instrumentMap = new Map<string, { childBotId: string; childBotName?: string }>();
  
  if (!config) {
    console.log('createInstrumentMapping: No config provided');
    return instrumentMap;
  }
  
  console.log('createInstrumentMapping: Config structure:', JSON.stringify(config, null, 2));
  
  // Support old structure with config.children
  if (config.children && typeof config.children === 'object') {
    console.log('createInstrumentMapping: Using old config.children structure');
    Object.entries(config.children).forEach(([childBotId, childConfig]: [string, any]) => {
      if (childConfig?.instrument) {
        const normalizedInstrument = normalizeInstrument(childConfig.instrument);
        instrumentMap.set(normalizedInstrument, { 
          childBotId,
          childBotName: childConfig.name || childBotId
        });
        
        // Add fallback mapping without USD for backward compatibility
        const fallbackInstrument = normalizedInstrument.replace(/USD/g, '');
        if (fallbackInstrument !== normalizedInstrument && fallbackInstrument) {
          instrumentMap.set(fallbackInstrument, { 
            childBotId,
            childBotName: childConfig.name || childBotId
          });
        }
        
        console.log(`createInstrumentMapping: Added mapping ${childConfig.instrument} -> ${normalizedInstrument} -> ${childBotId}`);
        if (fallbackInstrument !== normalizedInstrument && fallbackInstrument) {
          console.log(`createInstrumentMapping: Added fallback mapping ${fallbackInstrument} -> ${childBotId}`);
        }
      }
    });
  } 
  // Support new structure where config itself contains child bot data
  else if (typeof config === 'object') {
    console.log('createInstrumentMapping: Using new flat config structure');
    Object.entries(config).forEach(([key, value]: [string, any]) => {
      // Skip non-bot data
      if (key === 'child_bot_ids' || !value || typeof value !== 'object') {
        return;
      }
      
      // Check if this looks like a bot ID (UUID format)
      const isBotId = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(key);
      
      if (isBotId && value.instrument) {
        const normalizedInstrument = normalizeInstrument(value.instrument);
        instrumentMap.set(normalizedInstrument, { 
          childBotId: key,
          childBotName: value.name || key
        });
        
        // Add fallback mapping without USD for backward compatibility
        const fallbackInstrument = normalizedInstrument.replace(/USD/g, '');
        if (fallbackInstrument !== normalizedInstrument && fallbackInstrument) {
          instrumentMap.set(fallbackInstrument, { 
            childBotId: key,
            childBotName: value.name || key
          });
        }
        
        console.log(`createInstrumentMapping: Added mapping ${value.instrument} -> ${normalizedInstrument} -> ${key}`);
        if (fallbackInstrument !== normalizedInstrument && fallbackInstrument) {
          console.log(`createInstrumentMapping: Added fallback mapping ${fallbackInstrument} -> ${key}`);
        }
      }
    });
  }
  
  console.log('createInstrumentMapping: Final mapping:', Object.fromEntries(instrumentMap));
  return instrumentMap;
};

/**
 * Get child bot IDs from merge analysis config
 * Enhanced to support both old and new config structures with fallbacks
 */
export const getChildBotIds = (config: any, fallbackChildBotIds?: string[]): string[] => {
  if (!config) {
    console.log('getChildBotIds: No config provided, using fallback:', fallbackChildBotIds || []);
    return fallbackChildBotIds || [];
  }
  
  console.log('getChildBotIds: Config structure:', JSON.stringify(config, null, 2));
  
  // Support old structure with config.children
  if (config.children && typeof config.children === 'object') {
    const childIds = Object.keys(config.children);
    console.log('getChildBotIds: Found child IDs from config.children:', childIds);
    return childIds;
  }
  
  // Support new structure where config itself contains child bot data
  if (typeof config === 'object') {
    const childIds = Object.keys(config).filter(key => {
      // Skip non-bot data
      if (key === 'child_bot_ids' || !config[key] || typeof config[key] !== 'object') {
        return false;
      }
      
      // Check if this looks like a bot ID (UUID format)
      return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(key);
    });
    
    if (childIds.length > 0) {
      console.log('getChildBotIds: Found child IDs from flat config:', childIds);
      return childIds;
    }
  }
  
  // Use fallback from mergedRun.child_bot_ids if parsing failed
  if (fallbackChildBotIds && fallbackChildBotIds.length > 0) {
    console.log('getChildBotIds: Using fallback child_bot_ids:', fallbackChildBotIds);
    return fallbackChildBotIds;
  }
  
  console.log('getChildBotIds: No child bot IDs found');
  return [];
};